import { useApiTableColumns } from 'components/InstructorSubmissions/Table';
import { useTranslation } from 'react-i18next';
import { getSubmissionLmsSyncStatus } from 'utils/gradePushback';
import { getSubmissionGradedBy } from 'utils/helper';
import {
    getQuizSubmissionGradeSnapshot,
    getQuizSubmissionSection,
    getQuizSubmissionStatus,
    QuizSubmissionStatus,
} from 'utils/quizSubmissionList';

const compare = (getter) => (order, submissions, extras) => {
    if (!order) return submissions;
    return [...submissions].sort((a, b) => {
        const first = getter(a, extras);
        const second = getter(b, extras);
        if (first == null) return second == null ? 0 : 1;
        if (second == null) return -1;
        const result =
            typeof first === 'number' && typeof second === 'number'
                ? first - second
                : String(first).localeCompare(String(second), undefined, { numeric: true });
        return order === 'desc' ? -result : result;
    });
};

export const useQuizSubmissionTableColumns = () => {
    const { t } = useTranslation();
    // Quiz attempts contain responses instead of files; keep the shared LMS columns.
    const columns = useApiTableColumns([]).filter(({ id }) => id !== 'filename');
    const nameColumn = {
        ...columns[0],
        id: 'name',
        columnTitle: t('table.student-name', 'Student Name'),
        isFixed: false,
    };
    const comparators = {
        identifier: compare((submission) => submission.api?.user?.id),
        section: compare(getQuizSubmissionSection),
        status: compare(getQuizSubmissionStatus),
        grade: compare((submission, { assignment }) => getQuizSubmissionGradeSnapshot(submission, assignment)?.grade),
        'lms-sync-status': compare((submission, { assignment }) =>
            getQuizSubmissionStatus(submission) === QuizSubmissionStatus.REVIEWED
                ? getSubmissionLmsSyncStatus(submission, assignment)
                : null
        ),
        grader: compare((submission, { accountUsers }) =>
            getQuizSubmissionStatus(submission) === QuizSubmissionStatus.REVIEWED
                ? getSubmissionGradedBy(submission, accountUsers)
                : null
        ),
    };

    return [columns[0], nameColumn, ...columns.slice(1)].map((column) => ({
        ...column,
        comparator: comparators[column.id] || column.comparator,
    }));
};
